import { Chip } from '@mui/material'
import { DataGridPremium, GridColumnMenu, GridColumnMenuProps, GridCsvExportOptions, GridEventListener, useGridApiRef } from '@mui/x-data-grid-premium'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { SampleDataContext } from '../../../../context/CustomContext'
import { ChipConfidenceBackgroundColor, ChipConfidenceColor } from './MetabolicPathway.model'
import parse from 'html-react-parser'

export const enum ExpandState {
    expand,
    collapse,
    neutral,
}

export interface MethabolicPathwayGridProps {
    runId: string
    metabolicPathways: any[]
    showHierarchy: boolean
    loading: boolean
    expandState: ExpandState
}

export function MetabolicPathwayGrid(props: MethabolicPathwayGridProps) {
    const { runId, metabolicPathways, showHierarchy, loading, expandState } = props
    const { triggerExport, setTriggerExport } = useContext(SampleDataContext)
    const [isExpanded, setIsExpanded] = useState<boolean>(false)

    const apiRef = useGridApiRef()

    const builtColumns = useMemo(() => buildColumns(showHierarchy), [showHierarchy])
    const initialState = useMemo(() => initialStateBuilder(INITIAL_COLUMNS_VISIBILITY, apiRef), [apiRef])

    const isGroupExpandedByDefault = () => {
        return isExpanded
    }

    const onRowClick: GridEventListener<'rowClick'> = useCallback(
        (params) => {
            if ((params.id as string).startsWith('auto-generated')) {
                const rowNode = apiRef.current.getRowNode(params.id)
                if (rowNode && rowNode.type === 'group') {
                    apiRef.current.setRowChildrenExpansion(params.id, !rowNode.childrenExpanded)
                }
            }
        },
        [apiRef]
    )

    useEffect(() => {
        if (expandState !== ExpandState.neutral) {
            setIsExpanded(expandState === ExpandState.expand)
        }
    }, [expandState])

    useEffect(() => {
        apiRef.current.setColumnVisibility('name', !showHierarchy)
    }, [showHierarchy, apiRef])

    useEffect(() => {
        if (triggerExport) {
            const options: GridCsvExportOptions = {
                fileName: `FunctionalInference-${runId}`,
                fields: showHierarchy ? ['0', '1', '2', '3', '4', '5', '6', '7', 'name', 'asvsCount', 'abundance', 'confidence'] : ['name', 'asvsCount', 'abundance', 'confidence'],
            }
            apiRef.current.exportDataAsCsv(options)
            setTriggerExport(false)
        }
    }, [apiRef, triggerExport, setTriggerExport, runId, showHierarchy])

    return (
        <DataGridPremium
            getRowId={(r) => r.id}
            apiRef={apiRef}
            autoHeight
            pagination
            pageSizeOptions={[5, 10, 20, 50]}
            sortingOrder={['asc', 'desc']}
            rows={metabolicPathways || []}
            columns={builtColumns}
            initialState={initialState}
            loading={loading}
            disableRowSelectionOnClick
            isGroupExpandedByDefault={isGroupExpandedByDefault}
            onRowClick={onRowClick}
            groupingColDef={{
                availableAggregationFunctions: [],
                headerName: 'Hierarchy',
                leafField: 'name',
                flex: 1,
                minWidth: 650,
            }}
            slots={{
                noRowsOverlay: () => <NoPathwaysOverlay />,
                columnMenu: CustomColumnMenu,
            }}
            sx={{
                '--DataGrid-cellOffsetMultiplier': 2,
                '.MuiDataGrid-cell:not(:has(.MuiDataGrid-groupingCriteriaCell))': {
                    '&:has(.full)': {
                        paddingLeft: `${showHierarchy ? '65px' : '10px'}`,
                    },
                },
                '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                    outline: 'none',
                },
                '.MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                },
                '.keyword': {
                    backgroundColor: '#FFEBFF',
                    padding: '8px 0',
                },
                '.MuiDataGrid-row:has([data-testid="KeyboardArrowRightIcon"])': {
                    cursor: 'pointer',
                },
                '.MuiDataGrid-row:has([data-testid="ExpandMoreIcon"])': {
                    cursor: 'pointer',
                    '&:has(.level-0)': {
                        borderBottom: '2px solid rgba(27, 73, 212, 1)',
                    },
                },
                '.MuiDataGrid-row:not(:has(.level-0)):not(:has(.name))': {
                    backgroundImage: 'linear-gradient(rgba(240, 240, 255, 0.35), rgba(240, 240, 255, 0.1))',
                },
                '& .project-name': {
                    fontWeight: 'bold',
                },
                borderTop: 'none',
                borderRight: 'none',
                borderLeft: 'none',
                marginTop: '15px',
                width: '100%',
            }}
        />
    )
}

const buildColumns = (showHierarchy: boolean) => {
    const columns = showHierarchy
        ? [
              {
                  field: '0',
                  headerName: '0',
                  renderCell: (params) => renderGroup(params, 0),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: '1',
                  headerName: '1',
                  renderCell: (params) => renderGroup(params, 1),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: '2',
                  headerName: '2',
                  renderCell: (params) => renderGroup(params, 2),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: '3',
                  headerName: '3',
                  renderCell: (params) => renderGroup(params, 3),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: '4',
                  headerName: '4',
                  renderCell: (params) => renderGroup(params, 4),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: '5',
                  headerName: '5',
                  renderCell: (params) => renderGroup(params, 5),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: '6',
                  headerName: '6',
                  renderCell: (params) => renderGroup(params, 6),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: 'name',
                  headerName: 'Name',
                  renderCell: (params) => renderHTML(params.row.name, 'full'),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              {
                  field: 'asvsCount',
                  headerName: 'ASVs count',
                  width: 150,
                  availableAggregationFunctions: ['sum'],
              },
              {
                  field: 'abundance',
                  headerName: 'Relative abundance (%)',
                  availableAggregationFunctions: ['avg'],
                  width: 200,
                  valueFormatter: ({ value }) => renderAbundance(value),
              },
              {
                  field: 'confidence',
                  headerName: 'Confidence value',
                  flex: 1,
                  availableAggregationFunctions: [],
                  renderCell: (params) => renderConfidenceChip(params.row.confidence),
              },
          ]
        : [
              {
                  field: 'name',
                  headerName: 'Name',
                  flex: 3,
                  renderCell: (params) => renderHTML(params.row.name, 'name'),
                  valueFormatter: ({ value }) => renderExport(value),
              },
              { field: 'asvsCount', headerName: 'ASVs count', width: 150 },
              {
                  field: 'abundance',
                  headerName: 'Relative abundance (%)',
                  width: 200,
              },
              {
                  field: 'confidence',
                  headerName: 'Confidence value',
                  flex: 1,
                  renderCell: (params) => renderConfidenceChip(params.row.confidence),
              },
          ]
    return columns
}

const renderExport = (value: string): string => {
    return value
        ? value
              .replace(/<[^>]+>/g, '')
              .replace('&alpha;', '\u0391')
              .replace('&beta;', '\u0392')
              .replace('&delta;', '\u0394')
              .replace('&gamma;', '\u03B3')
        : ''
}

const renderAbundance = (value: any) => {
    if (!value) {
        return null
    }
    return Math.ceil(value * 1000) / 1000
}

const renderGroup = (params: any, level: number) => {
    return renderHTML(params.value, `group level-${level}`)
}

const renderHTML = (htmlString: string, classes?: string) => {
    return <div className={classes}>{parse(htmlString)}</div>
}

const renderConfidenceChip = (confidence: number) => {
    if (!confidence) {
        return null
    }

    const color = confidence < 75 ? ChipConfidenceColor.Red : confidence < 90 ? ChipConfidenceColor.Yellow : ChipConfidenceColor.Green
    const backgroundColor = confidence < 75 ? ChipConfidenceBackgroundColor.Red : confidence < 90 ? ChipConfidenceBackgroundColor.Yellow : ChipConfidenceBackgroundColor.Green

    return (
        <Chip
            style={{ paddingTop: '1px' }}
            label={`${confidence}%`}
            color={color}
            size="medium"
            variant="outlined"
            className="confidence-chip"
            sx={{ backgroundColor: backgroundColor }}
        />
    )
}

const NoPathwaysOverlay = () => {
    const [show, setShow] = useState(false)

    useEffect(() => {
        const timeout = setTimeout(() => setShow(true), 300)
        return () => clearTimeout(timeout)
    }, [])

    if (!show) return null
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
            }}
        >
            <p>No pathways found</p>
        </div>
    )
}

const initialStateBuilder = (columnVisibilityModel: any, apiRef: React.MutableRefObject<GridApiPremium>) => {
    return {
        apiRef,
        pagination: {
            paginationModel: { pageSize: 10, page: 0 },
        },
        rowGrouping: {
            model: ['0', '1', '2', '3', '4', '5', '6'],
        },
        columns: {
            columnVisibilityModel: columnVisibilityModel,
        },
    }
}

const INITIAL_COLUMNS_VISIBILITY = {
    '0': false,
    '1': false,
    '2': false,
    '3': false,
    '4': false,
    '5': false,
    '6': false,
    name: false,
}

function CustomColumnMenu(props: GridColumnMenuProps) {
    return (
        <GridColumnMenu
            {...props}
            slots={{
                columnMenuGroupingItem: null,
                columnMenuPinningItem: null,
                columnMenuColumnsItem: null,
            }}
        />
    )
}
